import React, {useEffect, useMemo, useState} from "react";
import {useFormik} from "formik";
import {useObserver} from "mobx-react-lite";
import {Input, Form, Modal, Select} from "antd";
import * as yup from "yup";
import {useTranslation} from "react-i18next";

import {TransferModalPropTypes} from "./TransferModalPropTypes";
import stores from "../../../../store/stores";

export const TransferModal = ({
    deliveryId,
    isVisible,
    setVisible,
    cancelFn
}: TransferModalPropTypes) => {
    const {t} = useTranslation(["transferModal"]);
    //#region variables
    const [availableCouriers, setAvailableCouriers] = useState(null);
    //#endregion

    //#region initialize funcs
    useEffect(() => {
        getAvailableCouriers();
    }, []);

    useEffect(() => {
        if (!isVisible) resetForm();
    }, [isVisible]);
    //#endregion

    //#region requests
    const getAvailableCouriers = async () => {
        const availableCouriers = await stores.orderStore.getAvailableCouriersForTransfer({
            deliveryId
        });
        setAvailableCouriers(availableCouriers);
    };

    const onSubmit = async values => {
        await stores.orderStore.transferOrder({
            deliveryId,
            courierId: (values.selectedCourier !== "" && values.selectedCourier) || undefined,
            transferReason: values.text,
            detail: values.detailText
        });

        setVisible(false);
    };
    //#endregion

    //#region views
    const formik = useFormik({
        initialValues: {text: "", detailText: "", selectedCourier: null},
        validationSchema: yup.object().shape({
            text: yup.string().notRequired(),
            detailText: yup.string().notRequired(),
            selectedCourier: yup
                .string()
                .notRequired()
                .nullable()
        }),
        onSubmit,
        enableReinitialize: false
    });

    const {
        values,
        errors,
        touched,
        handleChange,
        handleSubmit,
        isSubmitting,
        resetForm,
        setFieldValue
    } = formik;

    const renderReason = useMemo(() => {
        return (
            <Form.Item
                colon={false}
                label={t("transferModal.transferReason", {ns: "transferModal"})}
                labelAlign="left"
                validateStatus={errors.text && touched.text ? "error" : "success"}
                help={errors.text && touched.text ? errors.text : null}
            >
                <Input.TextArea
                    name="text"
                    value={values.text}
                    onChange={handleChange}
                    data-testid={`transferModal_textArea`}
                />
            </Form.Item>
        );
    }, [errors.text, touched.text, values.text, handleChange]);

    const renderExtraInfo = useMemo(() => {
        return (
            <Form.Item
                colon={false}
                validateStatus={errors.detailText && touched.detailText ? "error" : "success"}
                help={errors.detailText && touched.detailText ? errors.detailText : null}
                label={`${t("transferModal.fields.detailText", {ns: "transferModal"})}:`}
            >
                <Input.TextArea
                    name="detailText"
                    value={values.detailText}
                    onChange={handleChange}
                    data-testid={`transferModal_detailTextArea`}
                />
            </Form.Item>
        );
    }, [errors.detailText, touched.detailText, values.detailText, handleChange]);

    const renderAvailableCouriers = useMemo(() => {
        return (
            <Form.Item
                label={t("transferModal.fields.availableCouriers", {ns: "transferModal"})}
                validateStatus={
                    errors.selectedCourier && touched.selectedCourier ? "error" : "success"
                }
                help={
                    errors.selectedCourier && touched.selectedCourier
                        ? errors.selectedCourier
                        : null
                }
            >
                <Select
                    showSearch
                    name="selectedCourier"
                    data-testid="selectedCourier"
                    value={values.selectedCourier}
                    onChange={value => setFieldValue("selectedCourier", value)}
                    loading={availableCouriers === null}
                    disabled={availableCouriers === null}
                >
                    {availableCouriers?.map(courier => (
                        <Select.Option key={courier.id} value={courier.id}>
                            {`${courier.name} - ${courier.distance?.toFixed(1)} km`}
                        </Select.Option>
                    ))}
                </Select>
            </Form.Item>
        );
    }, [
        availableCouriers,
        errors.selectedCourier,
        touched.selectedCourier,
        values.selectedCourier,
        handleChange
    ]);

    return useObserver(() => (
        <Modal
            visible={isVisible}
            centered
            title={t("transferModal.modal.title", {ns: "transferModal"})}
            okText={t("modal.sendButton", {ns: "common"})}
            cancelText={t("modal.cancelButton2", {ns: "common"})}
            onOk={handleSubmit}
            data-testid={`transferModal`}
            okButtonProps={{
                disabled: isSubmitting || availableCouriers === null
            }}
            onCancel={() => {
                resetForm();
                setVisible();
                cancelFn();
            }}
        >
            <Form autoComplete="off" labelCol={{span: 24}} wrapperCol={{span: 24}}>
                {renderAvailableCouriers}
                {renderReason}
                {renderExtraInfo}
            </Form>
        </Modal>
    ));
    //#endregion
};

export default TransferModal;
